import { toResult, type Fetched, type V1Result } from './client';
import { ApiProblem } from './problem';

/** One page of a v1 list; `nextOffset` is null on the last page, `total` counts every page's items. */
export interface Page<T> {
  items: T[];
  nextOffset: number | null;
  total: number;
}

/** The paging parameters to spread into a list call's query. */
export interface PageParams {
  limit: number;
  offset: number;
}

/** The largest page the backend serves. */
export const PAGE_LIMIT = 500;

/** Stops a runaway walk; no editor read comes near it (the generic route returned at most 1000 rows). */
export const MAX_LIST_ITEMS = 20_000;

/** The most values one repeatable filter takes per request (backend MaxFilterValues / MaxIds). */
export const MAX_FILTER_VALUES = 100;

function refused(code: string, title: string): V1Result<never> {
  return { data: null, error: new ApiProblem({ status: 0, code, title }) };
}

/**
 * Every item of a list, following `nextOffset` page by page in the endpoint's order.
 *
 *   const { data, error } = await listAll(page => v1.GET('/api/v1/scenes', { params: { query: { projectUniqueId: [projectId], ...page } } }));
 */
export async function listAll<T>(
  fetchPage: (page: PageParams) => Promise<Fetched<Page<T>>>,
  options: { maxItems?: number } = {},
): Promise<V1Result<T[]>> {
  const maxItems = options.maxItems ?? MAX_LIST_ITEMS;
  const items: T[] = [];
  let offset = 0;
  for (;;) {
    const result = await toResult(fetchPage({ limit: PAGE_LIMIT, offset }));
    if (result.error) return { data: null, error: result.error };
    items.push(...result.data.items);
    const next = result.data.nextOffset;
    if (next == null) return { data: items, error: null };
    if (next <= offset) return refused('pagination_stalled', `The list did not advance past offset ${offset}.`);
    if (items.length >= maxItems) return refused('list_too_large', `The list has more than ${maxItems} items.`);
    offset = next;
  }
}

/** How many reads one helper keeps in flight at once. */
export const READ_CONCURRENCY = 4;

/**
 * Runs `task` for every input with at most `limit` in flight, the results in input order. Once a
 * task fails no new one starts, and that problem is the result.
 */
export async function mapConcurrently<I, T>(
  inputs: readonly I[],
  task: (input: I) => Promise<V1Result<T>>,
  limit = READ_CONCURRENCY,
): Promise<V1Result<T[]>> {
  const results: T[] = new Array(inputs.length);
  let next = 0;
  let failed: ApiProblem | null = null;
  const worker = async () => {
    while (!failed && next < inputs.length) {
      const index = next++;
      const result = await task(inputs[index]);
      if (result.error) failed ??= result.error;
      else results[index] = result.data;
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, inputs.length) }, worker));
  return failed ? { data: null, error: failed } : { data: results, error: null };
}

/**
 * Every item matching any of `values` on a repeatable filter (the generic route's `in.(...)`).
 * Values are de-duplicated and sent in chunks of at most `chunkSize`, a few chunks at a time
 * (READ_CONCURRENCY), each walked to its last page. No values means no items and no request: an
 * empty array would drop the filter from the query and widen it. Items come back chunk by chunk,
 * so pass `compare` when the caller needs one overall order (each chunk is only ordered within itself).
 *
 *   const { data, error } = await listAllIn(assetInstanceIds, (assetInstanceId, page) =>
 *     v1.GET('/api/v1/keyframes', { params: { query: { assetInstanceUniqueId: assetInstanceId, ...page } } }));
 */
export async function listAllIn<T>(
  values: readonly string[],
  fetchPage: (chunk: string[], page: PageParams) => Promise<Fetched<Page<T>>>,
  options: { chunkSize?: number; maxItems?: number; compare?: (a: T, b: T) => number } = {},
): Promise<V1Result<T[]>> {
  const chunkSize = Math.min(options.chunkSize ?? MAX_FILTER_VALUES, MAX_FILTER_VALUES);
  const maxItems = options.maxItems ?? MAX_LIST_ITEMS;
  const unique = [...new Set(values)];
  const chunks: string[][] = [];
  for (let start = 0; start < unique.length; start += chunkSize) chunks.push(unique.slice(start, start + chunkSize));
  let total = 0;
  const result = await mapConcurrently<string[], T[]>(chunks, async chunk => {
    const listed = await listAll(page => fetchPage(chunk, page), { maxItems });
    if (listed.error) return listed;
    total += listed.data.length;
    return total > maxItems ? refused('list_too_large', `The list has more than ${maxItems} items.`) : listed;
  });
  if (result.error) return { data: null, error: result.error };
  const items = result.data.flat();
  if (options.compare) items.sort(options.compare);
  return { data: items, error: null };
}
